import React, { useEffect, useState } from 'react';
import taskService, { WorkflowTask, TaskListResponse } from '../../services/taskService';
import { commonService } from '../../services/commonService';
import referenceFileService, { ReferenceFile } from '../../services/referenceFileService';
import { hasPermission, getMyUserId, isCXUser, isGoodwillApprover } from '../../utils/permissions';
import { capitalize } from '../../utils/formatters';
import StageStepper, { stageLabel } from './StageStepper';
import TaskChat from './TaskChat';
import ProjectActivityFeed from './ProjectActivityFeed';
import AssigneeTree from './AssigneeTree';
import { NEXT_STATUS, STATUS_LABEL } from './TaskWorkflowPanel';
import AddSubtaskModal from './AddSubtaskModal';
import SubtaskDetailModal from './SubtaskDetailModal';
import QaReviewModal from './QaReviewModal';
import SendDeliverModal from './SendDeliverModal';
import RejectProjectModal from './RejectProjectModal';
import FDCCreateWizard from '../../pages/DataCenter/FDC/FDCCreateWizard';
import '../../assets/styles/Workflow.css';

interface ProjectDetailOverlayProps {
  item: any;
  onClose: () => void;
  onChanged: () => void;
}

const STAGES = ['pending_build', 'in_progress', 'qa_review', 'ready_to_deliver', 'delivered'];

// Once a subtask reaches any of these, it's done being worked on - editing
// its title/requirements/priority/due date/assignee no longer makes sense.
const isTerminalTaskStatus = (status: string) => status === 'done' || status === 'qa_complete' || status === 'failed';

const formatDate = (dateString?: string) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-IN', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const slaChipClass = (label?: string) => {
  if (!label) return 'wf-chip2 wf-chip2-neutral';
  if (/overdue/i.test(label)) return 'wf-chip2 wf-chip2-crit';
  if (/met/i.test(label)) return 'wf-chip2 wf-chip2-ok';
  return 'wf-chip2 wf-chip2-neutral';
};

const priorityChipClass = (priority?: string | null) => {
  if (priority === 'high') return 'wf-chip2 wf-chip2-crit';
  if (priority === 'low') return 'wf-chip2 wf-chip2-ok';
  return 'wf-chip2 wf-chip2-neutral';
};

// Small inline badge mirroring the "hs-badge" style already used ad hoc in
// FDCCreateWizard.tsx - there is no globally-defined .hs-badge class in
// Workflow.css, so this is styled the same inline way rather than inventing
// a new shared class for a single decorative badge.
const HsBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      fontSize: 11,
      background: '#e8f0fe',
      color: '#3b82f6',
      padding: '3px 8px',
      borderRadius: 999,
      fontWeight: 700,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const ProjectDetailOverlay: React.FC<ProjectDetailOverlayProps> = ({ item, onClose, onChanged }) => {
  const myId = getMyUserId();
  const isAdmin = localStorage.getItem('role_id') === '1';
  // PC Manager never sees customer contact details or the Send/Deliver
  // popup - a found HubSpot lead is delivered to straight away, a missing
  // one is left for a Fulfillment user to fill in and send themselves.
  const isPcManager = localStorage.getItem('role_id') === '16';
  const isFulfillment = localStorage.getItem('role_id') === '19';
  const displaySummary = item.summary ? capitalize(item.summary) : item.summary;
  // Contacts/due date used to live inside the title text itself - now shown
  // as their own line below it instead, so the title stays a stable
  // identifier while these (genuinely changeable) facts stay current.
  const totalContacts = (item.criteria_groups || []).reduce(
    (sum: number, g: any) => sum + (parseInt(g?.number_of_contacts, 10) || 0), 0
  );

  const [editOpen, setEditOpen] = useState(false);
  const [tasksResp, setTasksResp] = useState<TaskListResponse | null>(null);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [sendingDirect, setSendingDirect] = useState(false);

  const [deliveryHistory, setDeliveryHistory] = useState<any[]>([]);

  const [addSubtaskOpen, setAddSubtaskOpen] = useState(false);
  const [subtaskDetail, setSubtaskDetail] = useState<WorkflowTask | null>(null);
  const [qaReviewTaskId, setQaReviewTaskId] = useState<number | null>(null);
  const [sendDeliverOpen, setSendDeliverOpen] = useState(false);
  const [uploadingProjectFiles, setUploadingProjectFiles] = useState(false);
  const [openFileMenuId, setOpenFileMenuId] = useState<number | null>(null);
  const [files, setFiles] = useState<any[]>(item.file || []);

  const [referenceFiles, setReferenceFiles] = useState<ReferenceFile[]>([]);
  const [uploadingReferenceFile, setUploadingReferenceFile] = useState(false);
  const [refFileActionId, setRefFileActionId] = useState<number | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  const [downloadingRefFileId, setDownloadingRefFileId] = useState<number | null>(null);

  const [customerDetails, setCustomerDetails] = useState<any[]>([]);
  const [copyingLink, setCopyingLink] = useState(false);
  // Only the latest recipient showed at all before - every earlier distinct
  // email this project was ever (re)sent to was already tracked server-side
  // but had no way to view it here. Collapsed by default since most
  // projects only ever have the one recipient.
  const [showEarlierRecipients, setShowEarlierRecipients] = useState(false);

  // Every Fulfillment and Finance user, always @mentionable regardless of
  // this specific project - see the mentionableUsers comment below.
  const [roleMentionUsers, setRoleMentionUsers] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    Promise.all([
      taskService.listPickableUsers('fulfillment'),
      taskService.listPickableUsers('Finance'),
    ]).then(([fulfillment, finance]) => {
      setRoleMentionUsers([...fulfillment, ...finance].map((u) => ({ id: u.id, name: u.name })));
    }).catch(() => setRoleMentionUsers([]));
  }, []);

  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(!!item.payment_completed);
  const [togglingPayment, setTogglingPayment] = useState(false);
  const [goodwillApprovedAt, setGoodwillApprovedAt] = useState<string | null>(item.goodwill_approved_at || null);
  const [approvingGoodwill, setApprovingGoodwill] = useState(false);
  const [cxComment, setCxComment] = useState('');
  const [cxActionLoading, setCxActionLoading] = useState(false);

  const [rightPanelTab, setRightPanelTab] = useState<'chat' | 'activity'>('chat');

  const togglePaymentCompleted = async () => {
    setError(null);
    setTogglingPayment(true);
    try {
      await commonService.fdcSetPaymentCompleted(item.id, !paymentCompleted);
      setPaymentCompleted(!paymentCompleted);
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to update payment status');
    } finally {
      setTogglingPayment(false);
    }
  };

  const approveGoodwill = async () => {
    setError(null);
    setApprovingGoodwill(true);
    try {
      const res: any = await commonService.fdcApproveGoodwill(item.id);
      setGoodwillApprovedAt(res?.goodwill_approved_at || new Date().toISOString());
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to approve Goodwill project');
    } finally {
      setApprovingGoodwill(false);
    }
  };

  const cxApprove = async () => {
    setError(null);
    setCxActionLoading(true);
    try {
      await commonService.fdcCxApprove(item.id, cxComment.trim() || undefined);
      setCxComment('');
      await loadTasks();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to approve this project');
    } finally {
      setCxActionLoading(false);
    }
  };

  const cxFlag = async () => {
    if (!cxComment.trim()) return;
    setError(null);
    setCxActionLoading(true);
    try {
      await commonService.fdcCxFlag(item.id, cxComment.trim());
      setCxComment('');
      await loadTasks();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to flag this project for correction');
    } finally {
      setCxActionLoading(false);
    }
  };

  const loadTasks = async () => {
    setLoadingTasks(true);
    try {
      const res = await taskService.getTasksByProject(item.id);
      setTasksResp(res);
    } catch (e) {
      setError('Failed to load workflow data');
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadDeliveryStatus = async () => {
    try {
      const res: any = await commonService.fdcStatus(item.projectid);
      setDeliveryHistory(res?.data || []);
    } catch (e) {
      setDeliveryHistory([]);
    }
  };

  const loadReferenceFiles = async () => {
    try {
      const refFiles = await referenceFileService.listForProject(item.id);
      setReferenceFiles(refFiles);
    } catch (e) {
      setReferenceFiles([]);
    }
  };

  // item.file comes from the parent list-row prop, which never refreshes
  // while this overlay stays open - independently refetch it (same fix
  // already applied to Reference/Files) so an upload/append/replace/delete
  // shows up immediately instead of needing the overlay closed and reopened.
  const loadFiles = async () => {
    try {
      const res: any = await commonService.fdcList({ project_id: item.id, page: 1, page_size: 1 });
      setFiles(res?.data?.[0]?.file || []);
    } catch (e) {
      // leave whatever was already loaded rather than blanking the list on a transient error
    }
  };

  // Who this project was actually delivered to - name/email/phone captured
  // at send time (cc_fdc_customer_email), most recent first. Only ever has
  // rows once at least one Send/Resend has happened.
  const loadCustomerDetails = async () => {
    // Backend gates this on fdc_customer:view - skip the call entirely for
    // roles that would just get a 403. Purely permission-driven (no role
    // hardcoded out) so whether a role - PC Manager included - can see
    // customer PII is entirely a Roles & Access decision, independent of
    // whether that same role can send (fdc_project:deliver, see
    // canSendDeliver above).
    if (!hasPermission('fdc_customer:view')) {
      setCustomerDetails([]);
      return;
    }
    try {
      const res: any = await commonService.fdcGetCustomerDetail(item.projectid);
      setCustomerDetails(res?.data || []);
    } catch (e) {
      setCustomerDetails([]);
    }
  };

  useEffect(() => {
    loadTasks();
    loadDeliveryStatus();
    loadFiles();
    loadReferenceFiles();
    loadCustomerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.projectid]);

  useEffect(() => {
    if (openFileMenuId === null) return;
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.wf-file-menu')) setOpenFileMenuId(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openFileMenuId]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 2000);
  };

  // One-click send for a role that can deliver but can't view customer PII
  // (fdc_project:deliver without fdc_customer:view) - the backend looks the
  // customer up in HubSpot itself and sends immediately, never returning
  // the details to this client. Used instead of opening SendDeliverModal
  // for exactly that role combination - see the button's onClick below.
  const handleQuickSend = async () => {
    setError(null);
    setSendingDirect(true);
    try {
      await commonService.fdcQuickSend(item.projectid);
      loadDeliveryStatus();
      onChanged();
      const isPartialRound = stage === 'ready_to_partially_deliver' || stage === 'partially_delivered';
      showSuccess(
        isPartialRound
          ? (stage === 'partially_delivered' ? 'Partial delivery resent to customer' : 'Partial delivery sent to customer')
          : (hasBeenSent ? 'Project resent to customer' : 'Project sent to customer')
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No customer detail found for this project.');
    } finally {
      setSendingDirect(false);
    }
  };

  const tasks = tasksResp?.tasks || [];
  const stage = tasksResp?.stage || item.stage || 'pending_build';
  const stageIdx = STAGES.indexOf(stage);
  const progress = stageIdx >= 0 ? Math.round(((stageIdx + 1) / STAGES.length) * 100) : 0;
  // Once Ready to (Partially) Deliver, Delivered, or Rejected, the project
  // is frozen - no more file changes or project edits, matching the same
  // lock the backend now enforces on /fdc/update and every file-mutation
  // endpoint. partially_delivered is deliberately NOT locked here - PC
  // Manager needs to keep working (upload the next batch, change stage
  // again) after a partial round completes.
  const isProjectLocked = stage === 'ready_to_partially_deliver' || stage === 'ready_to_deliver' || stage === 'delivered' || stage === 'rejected';

  // 5-day post-delivery reupload window, computed from the immutable first
  // 'Send' row (same anchor the backend uses in _get_delivered_at) so it
  // stays correct even if delivery happens while this overlay is already
  // open - deliveryHistory is refetched on load, item.reupload_deadline
  // (from the list page) is only a fallback for before that fetch lands.
  const sendRecord = deliveryHistory.find((h) => h.status === 'Send');
  const reuploadDeadline = sendRecord
    ? new Date(new Date(sendRecord.created_at).getTime() + 5 * 24 * 60 * 60 * 1000)
    : item.reupload_deadline
    ? new Date(item.reupload_deadline)
    : null;

  // No "root" task concept - every subtask is flat, freely-assigned
  // delegated work; the project's own assignee (FDCMaster.assignee_id)
  // already represents who's responsible by default, with no task needed
  // to represent that. subtaskRows is simply every task under the project.
  const subtaskRows = tasks;
  const isProjectAssignee = item.assignee_id === myId;
  const canDelegate = hasPermission('fdc_task:create_subtask') && (isProjectAssignee || isAdmin);
  const projectMembers = tasksResp?.project_members || [];
  const taskMembers = tasks.flatMap((t) => t.members || []);
  const mergedMembers = (() => {
    const byKey = new Map<string, { user_id: number; member_type: string; name: string | null }>();
    [...projectMembers, ...taskMembers].forEach((m) => {
      byKey.set(`${m.member_type}:${m.user_id}`, m);
    });
    return Array.from(byKey.values());
  })();
  const participants = mergedMembers.filter((m) => m.member_type === 'participant');
  const observers = mergedMembers.filter((m) => m.member_type === 'observer');
  const openQaTask = tasks.find(
    (t) => t.kind === 'qa' && t.assignee_id === myId && t.status !== 'qa_complete' && t.status !== 'done'
  );
  const openBuildTask = tasks.find(
    (t) => t.kind === 'build' && t.assignee_id === myId && t.status !== 'done'
  );
  // Send/Deliver (partial or final) only ever becomes actionable once the
  // project is at a "ready" or "just delivered" checkpoint AND Finance has
  // confirmed payment (one-time gate, covers every round). Purely
  // permission-driven (fdc_project:deliver) - no role is hardcoded out here
  // on purpose, so who can send is entirely a Roles & Access decision, not a
  // frontend one. Fulfillment owns delivery by default today only because
  // pc_manager's role doesn't currently hold this permission - not because
  // of anything in this file. A Fulfillment user's visibility into this
  // project at all is already handled server-side (fdc/list +
  // fulfillment_queue_condition, which no longer requires payment - they see
  // a ready project coming before Finance confirms payment, they just can't
  // send it yet, hence stageIsDeliverable/awaitingPayment below).
  const stageIsDeliverable =
    stage === 'ready_to_partially_deliver' || stage === 'partially_delivered' ||
    stage === 'ready_to_deliver' || stage === 'delivered';
  // Refulfillment projects never need a fresh Finance sign-off - the
  // customer already paid on the original fulfillment - so payment is
  // bypassed entirely for them, matching the same bypass in
  // _check_send_preconditions on the backend.
  const isRefulfillment = item.record_type === 'refulfillment';
  // Goodwill also skips Finance's payment sign-off, but has its own gate in
  // its place - Syed Abrar's approval (a single named person, not a role),
  // matching _check_send_preconditions on the backend.
  const isGoodwill = item.record_type === 'goodwill';
  const canSendDeliver =
    hasPermission('fdc_project:deliver') &&
    stageIsDeliverable &&
    (isGoodwill ? !!goodwillApprovedAt : (paymentCompleted || isRefulfillment));
  // Same viewer/stage gate as canSendDeliver, but payment is the one thing
  // missing - shown in place of the Send button so whoever would otherwise
  // send it knows why there's nothing to click yet, instead of the card
  // just disappearing. Fulfillment always qualifies; PC Manager only when
  // they actually hold fdc_project:deliver (e.g. via Quick-Send), same gate
  // canSendDeliver itself uses - a PC Manager without that permission was
  // never going to send this regardless of payment, so the note would be
  // irrelevant to them. Admin also holds the permission but isn't included
  // here - this nudge is for the role that would actually be blocked by it.
  // Never shown for refulfillment - there's nothing to wait for.
  const awaitingPayment =
    (isFulfillment || (isPcManager && hasPermission('fdc_project:deliver'))) &&
    stageIsDeliverable &&
    !paymentCompleted &&
    !isRefulfillment &&
    !isGoodwill;
  // Same idea as awaitingPayment, for Goodwill's own approval gate instead
  // of Finance's - shown to whoever would otherwise send it, so "why is
  // there no Send button" always has a visible answer.
  const isPendingGoodwillApproval = isGoodwill && stageIsDeliverable && !goodwillApprovedAt;
  // Syed Abrar approves by identity, not role, so this must be independent
  // of isFulfillment/isPcManager below - his own account's role (e.g.
  // Business Development) shouldn't block him from seeing or clicking this,
  // matching GOODWILL_APPROVER_EMAIL's single-named-approver intent on the
  // backend (_check_send_preconditions / approve-goodwill).
  const canApproveGoodwill = isPendingGoodwillApproval && isGoodwillApprover();
  const awaitingGoodwillApproval =
    isPendingGoodwillApproval &&
    !canApproveGoodwill &&
    (isFulfillment || (isPcManager && hasPermission('fdc_project:deliver')));
  // The Customer card is only worth showing if it has records to display OR
  // at least one of its action buttons/timeline would actually render -
  // otherwise it's just an empty, unactionable box for this viewer. Uses
  // canSendDeliver (not the bare permission) so this card stays hidden for
  // a deliver-permission holder until it's actually ready, rather than
  // showing up early as an empty shell with a disabled button.
  //
  // PC Manager is special-cased to ONLY the send-capable-or-about-to-be
  // condition, not the full OR-list below - a PC Manager granted
  // fdc_project:deliver should see the card exactly when they can act on it
  // (or are just waiting on Finance to be able to), but shouldn't also gain
  // the QA-review/survey/delivered-history views that condition is never
  // meant to unlock for this role. Everyone else keeps the full condition set.
  // Actual customer PII (name/email/phone) inside this card is a separate,
  // independent gate - see loadCustomerDetails, driven purely by
  // fdc_customer:view - so "can send" and "can see who they're sending to"
  // are two different permissions, not bundled together.
  const showCustomerSection = isPcManager
    ? (canSendDeliver || awaitingPayment || awaitingGoodwillApproval || canApproveGoodwill)
    : (
        canSendDeliver ||
        awaitingPayment ||
        awaitingGoodwillApproval ||
        canApproveGoodwill ||
        (hasPermission('fdc_task:qa_review') && !!openQaTask) ||
        !!(item.customer_email && item.brand_name === 'DataCaptive' && item.survey_status) ||
        stage === 'delivered' || stage === 'partially_delivered' ||
        deliveryHistory.length > 0
      );
  // Has this project already reached the customer at least once? Drives the
  // Send/Deliver button + modal relabeling to Resend, and the wording of the
  // success toast after it goes through.
  const hasBeenSent = deliveryHistory.some((h) => h.status === 'Send' || h.status === 'Resend');

  // Everyone @mentionable in the project-level chat: the creator, the
  // project's assignee, every task's assignee, plus participants/observers -
  // available from day one, even before any task has been created. Every
  // Fulfillment and Finance user is also always mentionable, regardless of
  // whether they're otherwise tied to this specific project - both roles
  // have broad, project-independent visibility (Finance reviews payment on
  // any project, Fulfillment delivers any ready_to_deliver+paid one), so
  // neither is reliably captured by the per-project relationships above.
  const mentionableUsers = (() => {
    const byId = new Map<number, { id: number; name: string }>();
    if (item.created_by_id) {
      byId.set(item.created_by_id, { id: item.created_by_id, name: item.created_by_name || `User #${item.created_by_id}` });
    }
    if (item.assignee_id) {
      byId.set(item.assignee_id, { id: item.assignee_id, name: item.assignee_name || `User #${item.assignee_id}` });
    }
    tasks.forEach((t) => {
      if (!byId.has(t.assignee_id)) byId.set(t.assignee_id, { id: t.assignee_id, name: t.assignee_name || `User #${t.assignee_id}` });
    });
    mergedMembers.forEach((m) => {
      if (!byId.has(m.user_id)) byId.set(m.user_id, { id: m.user_id, name: m.name || `User #${m.user_id}` });
    });
    roleMentionUsers.forEach((u) => {
      if (!byId.has(u.id)) byId.set(u.id, u);
    });
    return Array.from(byId.values());
  })();

  // PC Manager's manual, unconditional status control - deliberately has no
  // relationship to any task/subtask state. Free jump between any of the 5
  // pre-delivery stages, in any order, plus 'rejected' - a second terminal
  // stage alongside 'delivered', reached from the same dropdown but routed
  // through a small modal first since it requires a reason. 'delivered' and
  // 'partially_delivered' are intentionally absent - only reachable via the
  // real Send/Deliver flow below (Fulfillment's, not PC Manager's - they no
  // longer deliver at all) - and once the real terminal stage is reached,
  // the dropdown disappears and status is locked for good. Landing on
  // 'partially_delivered' does NOT lock it though - the dropdown stays
  // available so PC Manager can cycle back for the next batch.
  const MANUAL_STAGES = ['pending_build', 'in_progress', 'qa_review', 'ready_to_partially_deliver', 'ready_to_deliver', 'rejected'];
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  // item.rejection_reason comes from the list-page prop, which this overlay
  // doesn't independently refetch - loadTasks() (called right after
  // rejecting) refreshes `stage` but has no rejection_reason field at all,
  // so without this override the reason wouldn't show until the overlay is
  // closed and reopened with a fresh item.
  const [rejectionReasonOverride, setRejectionReasonOverride] = useState<string | null>(null);

  const changeStage = async (newStage: string) => {
    if (newStage === stage) return;
    if (newStage === 'rejected') {
      setRejectModalOpen(true);
      return;
    }
    setError(null);
    try {
      await taskService.setProjectStage(item.id, newStage);
      await loadTasks();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to change project status');
    }
  };

  const toggleSubtaskDone = async (task: WorkflowTask) => {
    const next = NEXT_STATUS[task.kind]?.[task.status];
    if (!next) return;
    setError(null);
    try {
      await taskService.updateTaskStatus(task.id, next);
      await loadTasks();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to update subtask');
    }
  };

  // Project-level upload - no task attribution. Multiple files upload one at
  // a time (each fully chunked + finalized before the next starts) rather
  // than in parallel, so chunk state never overlaps between files. Backend
  // ownership rule for a task-less upload: the project's assigned PC
  // Manager (or admin) - see _check_upload_ownership.
  const uploadFilesToProject = async (files: FileList | File[]) => {
    setUploadingProjectFiles(true);
    setError(null);
    // The project's PC Manager (or admin) uploads task-less, as always; a PC
    // uploading against their own delegated build subtask instead attributes
    // the file to that task, matching the backend's ownership rule.
    const uploadTaskId = (isAdmin || isProjectAssignee) ? null : openBuildTask?.id ?? null;
    try {
      const fileArray = Array.from(files);
      for (const file of fileArray) {
        const CHUNK_SIZE = 1 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const formData = new FormData();
          formData.append('projectid', item.projectid);
          formData.append('file_id', fileId);
          formData.append('file_name', file.name);
          formData.append('chunk_index', String(i));
          formData.append('total_chunks', String(totalChunks));
          formData.append('file', chunk);
          await commonService.uploadChunk(formData);
        }
        await commonService.finalUpload(item.projectid, uploadTaskId);
      }
      showSuccess(fileArray.length > 1 ? 'Files uploaded successfully' : 'File uploaded successfully');
      await loadFiles();
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'File upload failed');
    } finally {
      setUploadingProjectFiles(false);
    }
  };

  const downloadFile = async (file: any) => {
    setDownloadingFileId(file.id);
    setError(null);
    try {
      const response = await commonService.fdcDownloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name;
      link.click();
      window.URL.revokeObjectURL(url);
      showSuccess('File downloaded');
    } catch (e) {
      setError('Failed to download file');
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Same ownership rule the backend enforces: admin, or whoever's actually
  // allowed to write to this file - the assignee of the task it's attributed
  // to, or the project's own PC Manager for files with no task attribution.
  // Locked once Ready to Deliver/Delivered/Rejected, no exceptions - matches
  // _raise_if_project_locked on the backend, which admin doesn't bypass either.
  const canModifyFile = (f: any): boolean => {
    if (isProjectLocked) return false;
    if (isAdmin) return true;
    if (!hasPermission('fdc_task:upload')) return false;
    if (f.task_id != null) {
      const t = tasks.find((t) => t.id === f.task_id);
      return !!t && t.assignee_id === myId;
    }
    return isProjectAssignee;
  };

  // Re-upload (replace) only: once delivered, PC/PC Manager get a 5-day
  // grace window to replace a file and resend - matches the backend's
  // _raise_if_project_locked(allow_post_delivery_reupload=True). Admin isn't
  // time-boxed within that window either. Append/Delete stay on
  // canModifyFile above with no exception - only replacing is allowed here.
  const canReplaceFile = (f: any): boolean => {
    if (canModifyFile(f)) return true;
    if (stage !== 'delivered') return false;
    if (isAdmin) return true;
    if (!hasPermission('fdc_task:upload')) return false;
    if (!reuploadDeadline || reuploadDeadline <= new Date()) return false;
    if (f.task_id != null) {
      const t = tasks.find((t) => t.id === f.task_id);
      return !!t && t.assignee_id === myId;
    }
    return isProjectAssignee;
  };

  // Two ways in: the project's assigned PC Manager uploads task-less (matches
  // the backend's task-less ownership rule), or a PC with an open build
  // subtask uploads attributed to that task (matches the backend's
  // task-assignee ownership rule) - see uploadFilesToProject below for which
  // task_id actually gets sent.
  const canUploadFiles = !isProjectLocked && (isAdmin || (hasPermission('fdc_task:upload') && (isProjectAssignee || !!openBuildTask)));
  // File visibility (names/dates) is unrestricted; only the actual download
  // is role-gated, for now to admin + pc_manager.
  const canDownloadFiles = hasPermission('fdc_file:download');

  const [fileActionId, setFileActionId] = useState<number | null>(null);
  const appendOrReplaceFile = async (file: any, actionType: 'append' | 'replace', newFile: File, reason?: string) => {
    setFileActionId(file.id);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', newFile);
      formData.append('file_id', String(file.id));
      if (reason) formData.append('reason', reason);
      await commonService.fdcAppendOrReplace(formData, actionType);
      showSuccess(actionType === 'append' ? 'Data appended' : 'File replaced');
      await loadFiles();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || `Failed to ${actionType === 'append' ? 'append to' : 'replace'} file`);
    } finally {
      setFileActionId(null);
    }
  };

  const deleteFile = async (file: any) => {
    if (!window.confirm(`Delete "${file.file_name}"? This cannot be undone.`)) return;
    setFileActionId(file.id);
    setError(null);
    try {
      await commonService.fdcDeleteFile(file.id);
      showSuccess('File deleted');
      await loadFiles();
      onChanged();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to delete file');
    } finally {
      setFileActionId(null);
    }
  };

  // Reference/Files - separate from the tightly-gated Files section above.
  // Arbitrary supporting files (sample/enrichment/work files, anything),
  // open to anyone related to the project regardless of role: everyone can
  // view, upload, and download; delete is scoped to whoever uploaded a given
  // file (or admin), matching the backend's ownership check.
  const uploadReferenceFiles = async (files: FileList | File[]) => {
    setUploadingReferenceFile(true);
    setError(null);
    try {
      const fileArray = Array.from(files);
      for (const file of fileArray) {
        await referenceFileService.upload(item.id, file);
      }
      await loadReferenceFiles();
      showSuccess(fileArray.length > 1 ? 'Files uploaded successfully' : 'File uploaded successfully');
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to upload reference file');
    } finally {
      setUploadingReferenceFile(false);
    }
  };

  const downloadReferenceFile = async (file: ReferenceFile) => {
    setDownloadingRefFileId(file.id);
    setError(null);
    try {
      await referenceFileService.download(file.id, file.file_name);
      showSuccess('File downloaded');
    } catch (e) {
      setError('Failed to download file');
    } finally {
      setDownloadingRefFileId(null);
    }
  };

  const canDeleteReferenceFile = (f: ReferenceFile): boolean => isAdmin || f.uploaded_by === myId;

  const deleteReferenceFile = async (file: ReferenceFile) => {
    if (!window.confirm(`Delete "${file.file_name}"? This cannot be undone.`)) return;
    setRefFileActionId(file.id);
    setError(null);
    try {
      await referenceFileService.delete(file.id);
      showSuccess('File deleted');
      await loadReferenceFiles();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to delete file');
    } finally {
      setRefFileActionId(null);
    }
  };

  const sendSurvey = async () => {
    try {
      await commonService.fdcSendSurvey(item.projectid);
      showSuccess('Survey sent successfully');
      onChanged();
    } catch (e) {
      setError('Failed to send survey');
    }
  };

  // Generates (or looks up) this customer's sign-in/registration link and
  // copies it to the clipboard - existing customers get a sign-in link,
  // new ones get a registration link, exactly what /fdc/get-link decides
  // server-side from cc_users.
  const copyCustomerLink = async (email: string) => {
    setCopyingLink(true);
    setError(null);
    try {
      const res: any = await commonService.fdcLink(email);
      await navigator.clipboard.writeText(res.full_link);
      showSuccess(res.link_type === 'signin' ? 'Sign-in link copied' : 'Registration link copied');
    } catch (e: any) {
      setError(e?.response?.message || 'Failed to generate customer link');
    } finally {
      setCopyingLink(false);
    }
  };

  // Requirements is rich-text (CKEditor HTML) - a detached, unrendered div is
  // just used to pull its plain text back out for the clipboard.
  const htmlToPlainText = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  const copyCriteriaGroup = async (g: any, idx: number) => {
    // primary_industry/bd_additional_notes are current; industry/requirements
    // are the pre-restructure names, still present on older saved projects.
    const lines = [
      `Criteria ${idx + 1}`,
      `No. of Companies: ${g.number_of_companies || '—'}`,
      `No. of Contacts: ${g.number_of_contacts || '—'}`,
    ];
    if (g.primary_industry || g.industry) lines.push(`Primary Industry: ${g.primary_industry || g.industry}`);
    if (g.secondary_industry) lines.push(`Secondary Industry: ${g.secondary_industry}`);
    if (g.naics_code || g.naics_sic_code) lines.push(`NAICS Code: ${g.naics_code || g.naics_sic_code}`);
    if (g.sic_code) lines.push(`SIC Code: ${g.sic_code}`);
    if (g.company_keywords) lines.push(`Company Keywords: ${g.company_keywords}`);
    if (g.department) lines.push(`Department: ${g.department}`);
    if (g.company_size) lines.push(`Employee Size: ${g.company_size}`);
    if (g.revenue_size) lines.push(`Revenue Size: ${g.revenue_size}`);
    if (g.geography) lines.push(`Geography: ${g.geography}`);
    if (g.technology_used) lines.push(`Technology Used: ${g.technology_used}`);
    if (g.job_title) lines.push(`Job Titles: ${g.job_title}`);
    if (g.specialty) lines.push(`Specialty: ${g.specialty}`);
    if (g.data_fields) lines.push(`Data Fields: ${g.data_fields}`);
    const notes = g.bd_additional_notes || g.requirements;
    if (notes) {
      const plain = htmlToPlainText(notes);
      if (plain) lines.push(`BD Additional Notes: ${plain}`);
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      showSuccess('Criteria copied to clipboard');
    } catch {
      setError('Failed to copy criteria');
    }
  };

  return (
    <>
      <div className="wf-overlay">
        <div className="wf-modal wide">
          <div className="wf-modal-head">
            <div>
              <h2 style={{ fontSize: 13, fontWeight: 400 }}>{displaySummary}</h2>
              <div className="wf-mono" style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>{item.projectid}</div>
              <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>
                Created {formatDate(item.created_at)}
                {item.task_deadline_date && <> · Due {formatDate(item.task_deadline_date)}</>}
                {' · '}{totalContacts} contacts
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {!isProjectLocked && hasPermission('fdc_project:edit') && (isAdmin || item.created_by_id === myId || isProjectAssignee) && (
                <button className="wf-btn wf-btn-sm" onClick={() => setEditOpen(true)}>
                  ✎ Edit project
                </button>
              )}
              <button className="wf-iconbtn" onClick={onClose}>✕</button>
            </div>
          </div>

          <div className="wf-split">
            <div className="wf-split-left">
              {error && <div style={{ color: '#ef4444', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}

              <StageStepper stage={stage} />

              {isCXUser() && stage === 'pending_cx_review' && (
                <div className="wf-card wf-card-pad" style={{ background: '#f5f3ff', marginBottom: 14 }}>
                  <div className="wf-field-label" style={{ marginBottom: 8 }}>Customer Experience review</div>
                  <div className="wf-form">
                    <textarea
                      placeholder="Comment (required to flag for correction, optional to approve)"
                      value={cxComment}
                      onChange={(e) => setCxComment(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <button className="wf-btn wf-btn-primary" disabled={cxActionLoading} onClick={cxApprove}>
                      {cxActionLoading ? 'Working…' : '✓ Approve'}
                    </button>
                    <button
                      className="wf-btn"
                      style={{ borderColor: '#c2410c', color: '#c2410c' }}
                      disabled={cxActionLoading || !cxComment.trim()}
                      title={!cxComment.trim() ? 'Add a comment explaining what needs correcting' : undefined}
                      onClick={cxFlag}
                    >
                      ✎ Flag for Correction
                    </button>
                  </div>
                </div>
              )}

              <div style={{ height: 18 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {stage !== 'rejected' && stage !== 'pending_cx_review' && stage !== 'cx_correction_needed' && (
                  <>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <div className="wf-progressbar"><div style={{ width: `${progress}%` }} /></div>
                    </div>
                    <div className="wf-mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{progress}%</div>
                  </>
                )}
                <span className={slaChipClass(item.sla_label)}>{item.sla_label || '—'}</span>
                {hasPermission('fdc_project:mark_ready') && isProjectAssignee && stage !== 'delivered' && stage !== 'rejected' && (
                  <select
                    className="wf-stage-select"
                    value={stage}
                    onChange={(e) => changeStage(e.target.value)}
                  >
                    {MANUAL_STAGES.map((s) => {
                      const needsFile = (s === 'ready_to_deliver' || s === 'ready_to_partially_deliver') && files.length === 0;
                      return (
                        <option
                          key={s}
                          value={s}
                          disabled={needsFile}
                          title={needsFile ? 'Upload at least one file first' : undefined}
                        >
                          {stageLabel(s)}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              {hasPermission('fdc_project:toggle_payment') && (
                <>
                  <div style={{ height: 14 }} />
                  <div
                    className="wf-card wf-card-pad"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: paymentCompleted ? '#f0fdf4' : '#f8f9fa', border: paymentCompleted ? '1px solid #bbf7d0' : undefined }}
                  >
                    <div>
                      <div className="wf-k" style={{ marginBottom: 2 }}>Payment</div>
                      <div className="wf-v" style={{ color: paymentCompleted ? '#15803d' : undefined }}>
                        {paymentCompleted ? '✓ Payment completed' : 'Payment not yet completed'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`wf-btn ${paymentCompleted ? '' : 'wf-btn-primary'}`}
                      disabled={togglingPayment}
                      onClick={togglePaymentCompleted}
                    >
                      {togglingPayment ? 'Saving...' : paymentCompleted ? 'Mark as not completed' : 'Mark payment completed'}
                    </button>
                  </div>
                </>
              )}

              <div style={{ height: 22 }} />
              <div className="wf-section-title">
                <h3>Project details</h3>
                <HsBadge>⬡ HubSpot linked</HsBadge>
              </div>
              <div className="wf-detail-grid">
                <div><div className="wf-k">Brand</div><div className="wf-v">{item.brand_name || '—'}</div></div>
                {(item.record_type === 'refulfillment' || item.record_type === 'credit_fulfillment') && (
                  <div><div className="wf-k">{item.record_type === 'credit_fulfillment' ? 'Fulfillment ID' : 'Refulfillment ID'}</div><div className="wf-v wf-mono">{item.refulfillment_id || '—'}</div></div>
                )}
                <div><div className="wf-k">Created</div><div className="wf-v">{formatDate(item.created_at)}</div></div>
                <div><div className="wf-k">Last updated</div><div className="wf-v">{formatDate(item.updated_at)}</div></div>
              </div>

              {stage === 'rejected' && (item.rejection_reason || rejectionReasonOverride) && (
                <>
                  <div style={{ height: 14 }} />
                  <div className="wf-card wf-card-pad" style={{ background: '#fef2f2', border: '1px dashed #fecaca' }}>
                    <div className="wf-k" style={{ marginBottom: 6, color: '#b91c1c' }}>Rejection reason</div>
                    <div style={{ fontSize: 13 }}>{item.rejection_reason || rejectionReasonOverride}</div>
                  </div>
                </>
              )}

              {item.criteria_groups && item.criteria_groups.length > 0 && (
                <>
                  <div style={{ height: 14 }} />
                  <div className="wf-k" style={{ marginBottom: 6 }}>Criteria ({item.criteria_groups.length})</div>
                  <div className="wf-stack" style={{ gap: 10 }}>
                    {item.criteria_groups.map((g: any, idx: number) => (
                      <div key={idx} className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>Criteria {idx + 1}</span>
                          <button
                            type="button"
                            className="wf-btn wf-btn-sm"
                            onClick={() => copyCriteriaGroup(g, idx)}
                            title="Copy this criteria to clipboard"
                          >
                            📋 Copy
                          </button>
                        </div>
                        <div className="wf-criteria-detail-grid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                          <span className="wf-k">Primary Industry</span><span>{g.primary_industry || g.industry || '—'}</span>
                          <span className="wf-k">Secondary Industry</span><span>{g.secondary_industry || '—'}</span>
                          <span className="wf-k">NAICS Code</span><span>{g.naics_code || g.naics_sic_code || '—'}</span>
                          <span className="wf-k">SIC Code</span><span>{g.sic_code || '—'}</span>
                          <span className="wf-k">Company Keywords</span><span>{g.company_keywords || '—'}</span>
                          <span className="wf-k">Department</span><span>{g.department || '—'}</span>
                          <span className="wf-k">Employee Size</span><span>{g.company_size || '—'}</span>
                          <span className="wf-k">Revenue Size</span><span>{g.revenue_size || '—'}</span>
                          <span className="wf-k">Geography</span><span>{g.geography || '—'}</span>
                          <span className="wf-k">Technology Used</span><span>{g.technology_used || '—'}</span>
                          <span className="wf-k">Job Titles</span><span>{g.job_title || '—'}</span>
                          <span className="wf-k">Specialty</span><span>{g.specialty || '—'}</span>
                          <span className="wf-k">No. of Companies</span><span>{g.number_of_companies || '—'}</span>
                          <span className="wf-k">No. of Contacts</span><span>{g.number_of_contacts || '—'}</span>
                          <span className="wf-k">Data Fields</span><span>{g.data_fields || '—'}</span>
                        </div>
                        {(g.bd_additional_notes || g.requirements) && (
                          <div style={{ fontSize: 13, marginTop: 8 }} dangerouslySetInnerHTML={{ __html: g.bd_additional_notes || g.requirements }} />
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}


              {item.comment && (
                <>
                  <div style={{ height: 18 }} />
                  <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa', border: '1px dashed #D1D5DB' }}>
                    <div className="wf-k" style={{ marginBottom: 6 }}>Comment</div>
                    <div style={{ fontSize: 13 }}>{item.comment}</div>
                  </div>
                </>
              )}

              <div style={{ height: 22 }} />
              <div className="wf-section-title"><h3>Assignee tree</h3></div>
              <AssigneeTree
                tasks={tasks}
                projectAssigneeId={item.assignee_id}
                projectAssigneeName={item.assignee_name}
                creatorId={item.created_by_id}
                creatorName={item.created_by_name}
              />

              <div style={{ height: 22 }} />
              <div className="wf-section-title">
                <h3>Subtasks</h3>
                {canDelegate && (
                  <button className="wf-btn wf-btn-primary" onClick={() => setAddSubtaskOpen(true)}>+ Add subtask</button>
                )}
              </div>
              <div className="wf-card wf-card-pad">
                {loadingTasks && !tasksResp ? (
                  <div className="wf-empty-hint">Loading subtasks…</div>
                ) : subtaskRows.length === 0 ? (
                  <div className="wf-empty-hint">No subtasks yet.</div>
                ) : (
                  subtaskRows.map((st) => {
                    const isAssignee = st.assignee_id === myId || isAdmin;
                    const next = NEXT_STATUS[st.kind]?.[st.status];
                    const done = st.status === 'done' || st.status === 'qa_complete';
                    return (
                      <div key={st.id} className="wf-subtask-row" style={{ flexWrap: 'wrap' }}>
                        <div
                          className={`wf-subtask-check ${done ? 'done' : ''}`}
                          style={{ cursor: isAssignee && next ? 'pointer' : 'default' }}
                          onClick={() => { if (isAssignee && next) toggleSubtaskDone(st); }}
                        >
                          {done ? '✓' : ''}
                        </div>
                        <div style={{ flex: 1, minWidth: 200, cursor: 'pointer' }} onClick={() => setSubtaskDetail(st)}>
                          <div style={{ fontWeight: 600, ...(done ? { textDecoration: 'line-through', color: '#9ca3af' } : {}) }}>
                            {st.kind === 'qa' ? '🔍 ' : st.kind === 'deliver' ? '📦 ' : st.kind === 'other' ? '📌 ' : ''}
                            {st.title || (st.kind === 'qa' ? 'QA review' : st.kind === 'deliver' ? 'Deliver to customer' : st.kind === 'other' ? 'Other subtask' : 'Build subtask')}
                          </div>
                          <div className="wf-subtask-meta">
                            <span>{st.assignee_name || 'Unassigned'}</span>
                          </div>
                        </div>
                        <span className={priorityChipClass(st.priority)}>{st.priority ? st.priority.charAt(0).toUpperCase() + st.priority.slice(1) : 'Medium'}</span>
                        <span className={slaChipClass(st.sla_label)}>{st.sla_label || '—'}</span>
                        <span className={`wf-status-chip wf-status-${st.status}`}>{STATUS_LABEL[st.status] || st.status}</span>
                        <button className="wf-btn" onClick={() => setSubtaskDetail(st)}>View →</button>
                      </div>
                    );
                  })
                )}
              </div>

              <div style={{ height: 22 }} />
              <div className="wf-section-title">
                <h3>Files</h3>
                {isProjectLocked && stage === 'delivered' && reuploadDeadline && reuploadDeadline > new Date() && (
                  <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                    🔒 Delivered — files can still be re-uploaded (with a reason) until {formatDate(reuploadDeadline.toISOString())}
                  </span>
                )}
                {isProjectLocked && !(stage === 'delivered' && reuploadDeadline && reuploadDeadline > new Date()) && (
                  <span style={{ fontSize: 11.5, color: '#9ca3af' }}>🔒 Locked — {stageLabel(stage)}, no further changes</span>
                )}
                {canUploadFiles && (
                  <label className="wf-btn wf-btn-primary" style={{ cursor: uploadingProjectFiles ? 'not-allowed' : 'pointer' }}>
                    {uploadingProjectFiles ? 'Uploading…' : '+ Upload file(s)'}
                    <input
                      type="file"
                      accept=".csv"
                      multiple
                      style={{ display: 'none' }}
                      disabled={uploadingProjectFiles}
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) uploadFilesToProject(files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="wf-card wf-card-pad">
                {files && files.length > 0 ? (
                  files.map((f: any) => {
                    const busy = fileActionId === f.id || downloadingFileId === f.id;
                    return (
                      <div key={f.id} className="wf-filerow">
                        <span>📄</span>
                        <span className="wf-fname" title={f.file_name}>{f.file_name}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11.5 }}>{formatDate(f.created_at)}</span>
                        {(canDownloadFiles || canModifyFile(f) || canReplaceFile(f)) && (
                          <div className="wf-file-menu">
                            <button
                              type="button"
                              className="wf-file-menu-trigger"
                              disabled={busy}
                              onClick={() => setOpenFileMenuId(openFileMenuId === f.id ? null : f.id)}
                            >
                              {busy ? '…' : '⋮'}
                            </button>
                            {openFileMenuId === f.id && (
                              <div className="wf-file-menu-dropdown">
                                {canDownloadFiles && (
                                  <button
                                    type="button"
                                    className="wf-file-menu-item"
                                    onClick={() => { setOpenFileMenuId(null); downloadFile(f); }}
                                  >
                                    <span className="wf-file-menu-icon">⬇</span> Download
                                  </button>
                                )}
                                {canModifyFile(f) && (
                                  <label className="wf-file-menu-item" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}>
                                    <span className="wf-file-menu-icon">➕</span> Append
                                    <input
                                      type="file"
                                      accept=".csv"
                                      style={{ display: 'none' }}
                                      disabled={busy}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        setOpenFileMenuId(null);
                                        if (file) appendOrReplaceFile(f, 'append', file);
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                )}
                                {canReplaceFile(f) && (
                                  <label className="wf-file-menu-item" style={{ cursor: busy ? 'not-allowed' : 'pointer' }}>
                                    <span className="wf-file-menu-icon">🔁</span> Re-upload
                                    <input
                                      type="file"
                                      accept=".csv"
                                      style={{ display: 'none' }}
                                      disabled={busy}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        setOpenFileMenuId(null);
                                        if (!file) return;
                                        // Post-delivery reupload (5-day grace window) requires a
                                        // reason, matching the backend's mandatory-reason check.
                                        if (stage === 'delivered' && !canModifyFile(f)) {
                                          const reason = window.prompt('This project has already been delivered. Please provide a reason for re-uploading this file (this will be logged and the customer will be re-sent the updated file):');
                                          if (!reason || !reason.trim()) { e.target.value = ''; return; }
                                          appendOrReplaceFile(f, 'replace', file, reason.trim());
                                        } else {
                                          appendOrReplaceFile(f, 'replace', file);
                                        }
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                )}
                                {canModifyFile(f) && (
                                  <>
                                    <div className="wf-file-menu-divider" />
                                    <button
                                      type="button"
                                      className="wf-file-menu-item wf-file-menu-item-danger"
                                      disabled={busy}
                                      onClick={() => { setOpenFileMenuId(null); deleteFile(f); }}
                                    >
                                      <span className="wf-file-menu-icon">🗑</span> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="wf-empty-hint">No files uploaded yet.</div>
                )}
              </div>

              <div style={{ height: 22 }} />
              <div className="wf-section-title">
                <h3>Reference / Files</h3>
                <label className="wf-btn wf-btn-primary" style={{ cursor: uploadingReferenceFile ? 'not-allowed' : 'pointer' }}>
                  {uploadingReferenceFile ? 'Uploading…' : '+ Upload file(s)'}
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    disabled={uploadingReferenceFile}
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) uploadReferenceFiles(files);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              <div className="wf-card wf-card-pad">
                {referenceFiles.length > 0 ? (
                  referenceFiles.map((f) => {
                    const downloading = downloadingRefFileId === f.id;
                    const deleting = refFileActionId === f.id;
                    const busy = downloading || deleting;
                    return (
                      <div key={f.id} className="wf-filerow">
                        <span>📎</span>
                        <span className="wf-fname" title={f.file_name}>{f.file_name}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11.5 }}>{f.uploaded_by_name || 'Unknown'}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11.5 }}>{formatDate(f.created_at)}</span>
                        <button
                          type="button"
                          className="wf-file-menu-trigger"
                          disabled={busy}
                          onClick={() => downloadReferenceFile(f)}
                          title="Download"
                        >
                          {downloading ? '…' : '⬇'}
                        </button>
                        {canDeleteReferenceFile(f) && (
                          <button
                            type="button"
                            className="wf-file-menu-trigger"
                            disabled={busy}
                            onClick={() => deleteReferenceFile(f)}
                            title="Delete"
                          >
                            {deleting ? '…' : '🗑'}
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="wf-empty-hint">No reference files uploaded yet.</div>
                )}
              </div>

              <div style={{ height: 22 }} />
              <div className="wf-section-title"><h3>Participants &amp; observers</h3></div>
              <div className="wf-card wf-card-pad">
                <div className="wf-detail-grid">
                  <div>
                    <div className="wf-k">Participants</div>
                    <div className="wf-chip-row">
                      {participants.length ? (
                        participants.map((m) => <span key={m.user_id} className="wf-chip">{m.name || `User #${m.user_id}`}</span>)
                      ) : (
                        <span className="wf-empty-hint" style={{ padding: 0 }}>None yet</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="wf-k">Observers</div>
                    <div className="wf-chip-row">
                      {observers.length ? (
                        observers.map((m) => (
                          <span key={m.user_id} className="wf-chip">{m.name || `User #${m.user_id}`}</span>
                        ))
                      ) : (
                        <span className="wf-empty-hint" style={{ padding: 0 }}>None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ height: 22 }} />
              {showCustomerSection && (
              <>
              <div className="wf-section-title">
                <h3>Customer</h3>
                <HsBadge>⬡ from HubSpot</HsBadge>
              </div>
              <div className="wf-card wf-card-pad">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {canSendDeliver && (
                    <button
                      className="wf-btn wf-btn-primary"
                      disabled={sendingDirect}
                      onClick={() => {
                        // Can this viewer actually see who they'd be sending
                        // to? If so, keep the full modal (Fulfillment/admin
                        // path - review/edit the recipient before sending).
                        // If not (deliver granted without customer:view -
                        // e.g. PC Manager), there's nothing for them to
                        // review anyway, so skip straight to a one-click
                        // send that looks the customer up in HubSpot
                        // server-side and never shows it to this client.
                        if (hasPermission('fdc_customer:view')) {
                          setSendDeliverOpen(true);
                        } else {
                          handleQuickSend();
                        }
                      }}
                    >
                      {sendingDirect
                        ? 'Sending…'
                        : stage === 'partially_delivered'
                        ? '↻ Resend Partial Delivery'
                        : stage === 'ready_to_partially_deliver'
                        ? 'Send Partial Delivery'
                        : hasBeenSent
                        ? '↻ Resend'
                        : 'Send / Deliver'}
                    </button>
                  )}
                  {awaitingPayment && (
                    <div className="wf-lockrow" style={{ margin: 0 }}>
                      ⏳ Get it approved by Finance before this can be sent to the customer
                    </div>
                  )}
                  {canApproveGoodwill && (
                    <button className="wf-btn wf-btn-primary" disabled={approvingGoodwill} onClick={approveGoodwill}>
                      {approvingGoodwill ? 'Approving…' : 'Approve Goodwill project'}
                    </button>
                  )}
                  {awaitingGoodwillApproval && (
                    <div className="wf-lockrow" style={{ margin: 0 }}>
                      ⏳ Waiting on Syed Abrar's approval before this Goodwill project can be sent to the customer
                    </div>
                  )}
                  {hasPermission('fdc_task:qa_review') && openQaTask && (
                    <button className="wf-btn" style={{ borderColor: '#3b82f6', color: '#3b82f6' }} onClick={() => setQaReviewTaskId(openQaTask.id)}>
                      Run QA review
                    </button>
                  )}
                  {(isFulfillment || isAdmin) && item.customer_email && item.brand_name === 'DataCaptive' && item.survey_status && (
                    <button className="wf-btn" onClick={sendSurvey}>➤ {item.survey_status}</button>
                  )}
                </div>

                {customerDetails.length > 0 && (() => {
                  const latest = customerDetails[0];
                  return (
                    <>
                      <div style={{ height: 14 }} />
                      <div className="wf-customer-detail">
                        <div className="wf-customer-detail-avatar">
                          {(latest.name || latest.email || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{latest.name || 'Unnamed contact'}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {latest.email}{latest.phone ? ` · ${latest.phone}` : ''}
                          </div>
                        </div>
                        <button
                          className="wf-btn wf-btn-sm"
                          disabled={copyingLink}
                          onClick={() => copyCustomerLink(latest.email)}
                          title="Copy this customer's sign-in / registration link"
                        >
                          {copyingLink ? '…' : '🔗 Copy link'}
                        </button>
                      </div>

                      {customerDetails.length > 1 && (
                        <div style={{ marginTop: 8 }}>
                          <button
                            className="wf-btn wf-btn-sm"
                            onClick={() => setShowEarlierRecipients((v) => !v)}
                          >
                            {showEarlierRecipients ? '▾' : '▸'} {customerDetails.length - 1} earlier recipient{customerDetails.length - 1 > 1 ? 's' : ''}
                          </button>
                          {showEarlierRecipients && (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {customerDetails.slice(1).map((c, idx) => (
                                <div key={c.id ?? idx} className="wf-customer-detail" style={{ opacity: 0.75 }}>
                                  <div className="wf-customer-detail-avatar">
                                    {(c.name || c.email || '?').trim().charAt(0).toUpperCase()}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{c.name || 'Unnamed contact'}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                      {c.email}{c.phone ? ` · ${c.phone}` : ''}
                                    </div>
                                  </div>
                                  <button
                                    className="wf-btn wf-btn-sm"
                                    disabled={copyingLink}
                                    onClick={() => copyCustomerLink(c.email)}
                                    title="Copy this customer's sign-in / registration link"
                                  >
                                    {copyingLink ? '…' : '🔗 Copy link'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {deliveryHistory.filter((h) => h.status === 'Partial Send').length > 0 && (
                  <>
                    <div style={{ height: 14 }} />
                    <div className="wf-k" style={{ marginBottom: 8 }}>Partial deliveries</div>
                    {deliveryHistory
                      .filter((h) => h.status === 'Partial Send')
                      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                      .map((h, i) => (
                        <div key={h.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <span
                            style={{
                              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                              background: '#0369a1', color: '#fff', fontSize: 11, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            {i + 1}
                          </span>
                          <div style={{ fontSize: 12.5 }}>
                            Batch {i + 1} sent <span style={{ color: '#9ca3af' }}>· {formatDate(h.created_at)}</span>
                          </div>
                        </div>
                      ))}
                  </>
                )}

                {(stage === 'delivered' || deliveryHistory.some((h) => h.status === 'Send' || h.status === 'Resend')) && (
                  <>
                    <div style={{ height: 14 }} />
                    <div className="wf-k" style={{ marginBottom: 8 }}>Delivery status</div>
                    {['Send', 'Resend', 'Viewed', 'Downloaded'].map((status, index) => {
                      const found = deliveryHistory.find((s) => s.status === status);
                      return (
                        <div key={status} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 18, position: 'relative' }}>
                          {index !== 3 && (
                            <div style={{ position: 'absolute', left: 11, top: 24, width: 2, height: 30, backgroundColor: '#e5e7eb' }} />
                          )}
                          <div
                            style={{
                              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                              backgroundColor: found ? '#10b981' : '#e5e7eb', color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, marginRight: 12,
                            }}
                          >
                            {found ? '✓' : ''}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 12.5, color: found ? '#10b981' : '#6b7280' }}>
                              {status === 'Send' ? 'Sent' : status === 'Resend' ? 'Resent' : status}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2 }}>
                              {found ? formatDate(found.created_at) : 'Pending'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              </>
              )}
            </div>

            <div className="wf-split-right">
              <div style={{ display: 'flex', gap: 4, padding: '14px 16px 0' }}>
                <button
                  type="button"
                  className={`wf-btn wf-btn-sm${rightPanelTab === 'chat' ? ' wf-btn-primary' : ''}`}
                  onClick={() => setRightPanelTab('chat')}
                >
                  💬 Chat
                </button>
                <button
                  type="button"
                  className={`wf-btn wf-btn-sm${rightPanelTab === 'activity' ? ' wf-btn-primary' : ''}`}
                  onClick={() => setRightPanelTab('activity')}
                >
                  🕘 Activity
                </button>
              </div>
              {rightPanelTab === 'chat' ? (
                <TaskChat projectId={item.id} variant="panel" mentionableUsers={mentionableUsers} />
              ) : (
                <ProjectActivityFeed projectId={item.id} />
              )}
            </div>
          </div>
        </div>
      </div>

      {editOpen && (
        <FDCCreateWizard
          editItem={item}
          onClose={() => setEditOpen(false)}
          onCreated={() => {
            // The header/details grid come straight from the `item` prop,
            // which this overlay doesn't independently refetch - closing
            // the whole overlay (after refreshing the parent list) is the
            // simplest way to guarantee the next open shows the saved data,
            // rather than silently leaving stale fields visible underneath.
            setEditOpen(false);
            onChanged();
            onClose();
          }}
        />
      )}

      {addSubtaskOpen && (
        <AddSubtaskModal
          projectId={item.id}
          projectSummary={displaySummary}
          maxDueDate={item.task_deadline_date ? String(item.task_deadline_date).slice(0, 10) : undefined}
          onClose={() => setAddSubtaskOpen(false)}
          onCreated={() => { setAddSubtaskOpen(false); loadTasks(); onChanged(); }}
        />
      )}

      {subtaskDetail && (
        <SubtaskDetailModal
          task={subtaskDetail}
          projectSummary={displaySummary}
          canEdit={(subtaskDetail.assignee_id === myId || subtaskDetail.created_by === myId || isAdmin) && !isTerminalTaskStatus(subtaskDetail.status)}
          maxDueDate={item.task_deadline_date ? String(item.task_deadline_date).slice(0, 10) : undefined}
          onClose={() => setSubtaskDetail(null)}
          onUpdated={(updated) => {
            if (updated) {
              setTasksResp((prev) => (prev ? { ...prev, tasks: prev.tasks.map((t) => (t.id === updated.id ? updated : t)) } : prev));
            }
            loadTasks();
            onChanged();
          }}
        />
      )}

      {qaReviewTaskId != null && (
        <QaReviewModal
          qaTaskId={qaReviewTaskId}
          projectSummary={displaySummary}
          onClose={() => setQaReviewTaskId(null)}
          onApproved={() => { loadTasks(); onChanged(); }}
        />
      )}

      {sendDeliverOpen && (
        <SendDeliverModal
          item={item}
          isResend={stage === 'partially_delivered' || (stage !== 'ready_to_partially_deliver' && hasBeenSent)}
          isPartial={stage === 'ready_to_partially_deliver' || stage === 'partially_delivered'}
          onClose={() => setSendDeliverOpen(false)}
          onSent={() => {
            loadDeliveryStatus();
            loadCustomerDetails();
            onChanged();
            const isPartialRound = stage === 'ready_to_partially_deliver' || stage === 'partially_delivered';
            showSuccess(
              isPartialRound
                ? (stage === 'partially_delivered' ? 'Partial delivery resent to customer' : 'Partial delivery sent to customer')
                : (hasBeenSent ? 'Project resent to customer' : 'Project sent to customer')
            );
          }}
        />
      )}

      {rejectModalOpen && (
        <RejectProjectModal
          projectId={item.id}
          projectSummary={displaySummary}
          onClose={() => setRejectModalOpen(false)}
          onRejected={(reason) => { setRejectionReasonOverride(reason); loadTasks(); onChanged(); }}
        />
      )}

      {successMsg && (
        <div
          style={{
            position: 'fixed', top: '50%', right: '37%', backgroundColor: '#28a745', color: '#fff',
            padding: '12px 20px', borderRadius: 8, boxShadow: '0px 4px 10px rgba(0,0,0,0.2)', fontSize: 15, zIndex: 9999,
          }}
        >
          {successMsg}
        </div>
      )}
    </>
  );
};

export default ProjectDetailOverlay;
